import {
    useFloating,
} from '@pb/kit-component-floating';

import {getItemsHeight} from './utils/get-items-height';

import type {Placement} from '@floating-ui/react';
import type {UseFloatingProps, UseFloatingReturnType, getFloatingElPropsReturnType} from '@pb/kit-component-floating';
import type {ComponentProps} from 'react';


const placements = {
    left: ['bottom-end', 'top-end'] as Placement[],
    right: ['bottom-start', 'top-start'] as Placement[],
};
const defaultAllowedPlacements = placements.right;

interface getListBoxPropsReturnType extends getFloatingElPropsReturnType {
    activeIndex: number | null,
    getItemProps: UseFloatingReturnType['getItemProps'],
    floatingElRef: (node: HTMLElement | null) => void,
}
export interface UseListboxProps extends UseFloatingProps {
    /**
     * If present, width does not equal to the input width, but extended to this direction
     */
    direction?: 'left' | 'right',
    /**
     * Controls height of the Listbox
     */
    itemsForScroll?: number,
}

type _UseFloatingReturnType = Omit<UseFloatingReturnType, 'getFloatingElProps' | 'activeIndex' | 'getItemProps' | 'floatingElRef' | 'setActiveIndex'>;

interface UseListboxReturnType extends _UseFloatingReturnType {
    getListBoxProps: (nativeProps?: ComponentProps<'div'>) => getListBoxPropsReturnType,
    setActiveIndex: (index: (number | null)) => void,
}

/**
 * Hook to use Listbox component
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/utils-listbox--documentation}
 * */
export function useListbox({
    direction,
    itemsForScroll = 10,
    allowedPlacements = direction ? placements[direction] : defaultAllowedPlacements,
    clickEnabled = true,
    listNavigationEnabled = true,
    equalWidth = !direction,
    typeaheadEnabled,
    maxHeight = getItemsHeight(itemsForScroll),
    role = 'combobox',
    listNavigationProps,
    ...restProps
}: UseListboxProps): UseListboxReturnType {

    const {
        getFloatingElProps,
        activeIndex,
        setActiveIndex,
        getItemProps,
        floatingElRef,
        ...rest
    } = useFloating({
        clickEnabled,
        listNavigationEnabled,
        typeaheadEnabled,
        allowedPlacements,
        equalWidth,
        maxHeight,
        role,
        listNavigationProps: {
            virtual: true,
            nested: false,
            ...listNavigationProps,
        },
        ...restProps,
    });

    return {
        getListBoxProps: nativeProps => ({
            activeIndex,
            getItemProps,
            floatingElRef,
            ...getFloatingElProps(nativeProps),
        }),
        setActiveIndex,
        ...rest,
    };
}
